import { ElementInfo, ElementWithUserNames, ElementType, ElementSceneInfo } from '../types/element.types';

/**
 * ElementType 枚举顺序映射
 * 按照后端实体文件中定义的顺序进行排序
 */
const ELEMENT_TYPE_ORDER: Record<ElementType, number> = {
  [ElementType.CAST]: 1,                         // 主要角色
  [ElementType.FEATURED]: 2,                     // 特约角色
  [ElementType.BACKGROUND]: 3,             // 群众演员
  [ElementType.STUNT_PLAYERS]: 4,                      // 动作演员
  [ElementType.ANIMALS]: 5,                            // 动物
  [ElementType.VEHICLES]: 6,                          // 车辆
  [ElementType.ART_PROPS]: 7,                         // 美术道具
  [ElementType.WARDROBE]: 8,                          // 服装
  [ElementType.HAIR_MAKEUP]: 9,                           // 梳头化妆
  [ElementType.SFX_MU]: 10,                      // 特效化妆
  [ElementType.CAMERA]: 11,                          // 摄影
  [ElementType.LIGHTING]: 12,                       // 灯光
  [ElementType.SFX]: 13,              // 现场特效
  [ElementType.VFX]: 14,                            // 电脑特效
  [ElementType.SOUND]: 15,                          // 收音
  [ElementType.ADDL_LABOR]: 16,                 // 外增人员
  [ElementType.SPECIAL_EQUIPMENT]: 17,               // 特殊器材
  [ElementType.MISC]: 18,                          // 其他
  [ElementType.NOTE]: 19                            // 备注
};

/**
 * 获取场次元素类型的排序权重
 * @param elementType 场次元素类型
 * @returns 排序权重，未定义的类型返回最大值
 */
const getElementTypeOrder = (elementType?: ElementType): number => {
  if (!elementType) return 999; // 未定义类型排最后
  return ELEMENT_TYPE_ORDER[elementType] || 999;
};

/**
 * 场次元素排序比较函数
 * 排序规则：
 * 1. 按 ElementType 枚举值顺序排序
 * 2. 相同类型：
 *    - 如果是主要角色类型，按简称排序（中文拼音、英文字母排序），如果没有简称则按名称排序
 *    - 其他类型按名称排序（支持中文拼音排序）
 * @param a 第一个场次元素
 * @param b 第二个场次元素
 * @returns 比较结果
 */
export const compareElements = (a: ElementWithUserNames, b: ElementWithUserNames): number => {
  // 1. 首先按类型排序
  const typeOrderA = getElementTypeOrder(a.elementType);
  const typeOrderB = getElementTypeOrder(b.elementType);
  
  if (typeOrderA !== typeOrderB) {
    return typeOrderA - typeOrderB;
  }
  
  // 2. 类型相同，如果是主要角色（CAST），按 elementNO 排序
  if (a.elementType === ElementType.CAST && b.elementType === ElementType.CAST) {
    return compareCastItemsByNO(a, b);
  }
  
  // 3. 其他类型，按名称排序（数字、英文、中文优先级）
  const nameA = a.elementName || '';
  const nameB = b.elementName || '';

  return compareExportNamesDigitLetterPinyin(nameA, nameB);
};

/**
 * 对场次元素数组进行排序
 * @param elements 场次元素数组
 * @returns 排序后的场次元素数组
 */
export const sortElements = (elements: ElementWithUserNames[]): ElementWithUserNames[] => {
  return [...elements].sort(compareElements);
};

/**
 * 检查字符串是否包含中文字符
 * @param str 要检查的字符串
 * @returns 是否包含中文
 */
export const containsChinese = (str: string): boolean => {
  return /[\u4e00-\u9fa5]/.test(str);
};

/**
 * 获取场次元素类型的显示顺序
 * @param elementType 场次元素类型
 * @returns 显示顺序（从1开始）
 */
export const getElementTypeDisplayOrder = (elementType?: ElementType): number => {
  return getElementTypeOrder(elementType);
};

type ExportNameTokenType = 'symbol' | 'number' | 'english' | 'chinese';

interface ExportNameToken {
  type: ExportNameTokenType;
  value: string;
}

const EXPORT_SYMBOL_PRIORITY: Record<string, number> = {
  "'": 0,
  ' ': 1,
  '#': 2,
  '/': 3,
  '-': 4,
  '_': 5,
  '&': 6,
  '+': 7,
  '.': 8,
  '@': 9,
};

const EXPORT_NAME_TYPE_PRIORITY: Record<ExportNameTokenType, number> = {
  symbol: 0,
  number: 1,
  english: 2,
  chinese: 3,
};

const getExportNameTokenType = (char: string): ExportNameTokenType => {
  if (/\d/.test(char)) return 'number';
  if (/[A-Za-z]/.test(char)) return 'english';
  if (/[\u4e00-\u9fa5]/.test(char)) return 'chinese';
  return 'symbol';
};

const tokenizeExportName = (input: string): ExportNameToken[] => {
  const text = (input || '').trim();
  if (!text) return [];
  const tokens: ExportNameToken[] = [];

  for (const char of text) {
    const currentType = getExportNameTokenType(char);
    const last = tokens[tokens.length - 1];
    if (!last || last.type !== currentType) {
      tokens.push({ type: currentType, value: char });
      continue;
    }
    last.value += char;
  }

  return tokens;
};

const compareSymbolChar = (a: string, b: string): number => {
  if (a === b) return 0;
  const rankA = Object.prototype.hasOwnProperty.call(EXPORT_SYMBOL_PRIORITY, a)
    ? EXPORT_SYMBOL_PRIORITY[a]
    : Number.MAX_SAFE_INTEGER;
  const rankB = Object.prototype.hasOwnProperty.call(EXPORT_SYMBOL_PRIORITY, b)
    ? EXPORT_SYMBOL_PRIORITY[b]
    : Number.MAX_SAFE_INTEGER;

  if (rankA !== rankB) return rankA - rankB;
  return a.localeCompare(b, 'zh-CN', { numeric: true, sensitivity: 'variant' });
};

const compareSymbolToken = (a: string, b: string): number => {
  const minLength = Math.min(a.length, b.length);

  for (let i = 0; i < minLength; i += 1) {
    const diff = compareSymbolChar(a[i], b[i]);
    if (diff !== 0) return diff;
  }

  return a.length - b.length;
};

const normalizeNumericToken = (value: string): string => {
  const stripped = value.replace(/^0+/, '');
  return stripped === '' ? '0' : stripped;
};

const compareNumberToken = (a: string, b: string): number => {
  const normalizedA = normalizeNumericToken(a);
  const normalizedB = normalizeNumericToken(b);

  if (normalizedA.length !== normalizedB.length) {
    return normalizedA.length - normalizedB.length;
  }
  if (normalizedA !== normalizedB) {
    return normalizedA.localeCompare(normalizedB);
  }
  if (a.length !== b.length) {
    return a.length - b.length;
  }
  return a.localeCompare(b);
};

const compareEnglishToken = (a: string, b: string): number =>
  a.localeCompare(b, 'en', { sensitivity: 'base', caseFirst: 'upper' });

const compareChineseToken = (a: string, b: string): number =>
  a.localeCompare(b, 'zh-Hans-u-co-pinyin', { numeric: true, sensitivity: 'base' });

const compareExportNameToken = (a: ExportNameToken, b: ExportNameToken): number => {
  if (a.type !== b.type) {
    return EXPORT_NAME_TYPE_PRIORITY[a.type] - EXPORT_NAME_TYPE_PRIORITY[b.type];
  }
  if (a.type === 'symbol') return compareSymbolToken(a.value, b.value);
  if (a.type === 'number') return compareNumberToken(a.value, b.value);
  if (a.type === 'english') return compareEnglishToken(a.value, b.value);
  return compareChineseToken(a.value, b.value);
};

const compareBySymbolNumberEnglishChinese = (a: string, b: string): number => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;

  const tokensA = tokenizeExportName(a);
  const tokensB = tokenizeExportName(b);
  const minLength = Math.min(tokensA.length, tokensB.length);

  for (let i = 0; i < minLength; i += 1) {
    const diff = compareExportNameToken(tokensA[i], tokensB[i]);
    if (diff !== 0) return diff;
  }

  if (tokensA.length !== tokensB.length) {
    return tokensA.length - tokensB.length;
  }

  return a.localeCompare(b, 'zh-CN', { numeric: true, sensitivity: 'base' });
};

/**
 * 导出/列表名称排序：符号 > 数字 > 英文 > 汉字(拼音)
 * 与 SceneCodeUtils 的分词比较思路保持一致：按连续同类字符分段逐段比较。
 */
export const compareExportNamesDigitLetterPinyin = (a: string, b: string): number => {
  return compareBySymbolNumberEnglishChinese(a, b);
};

export const sortStringsByExportNameRule = (arr: string[]): string[] =>
  [...arr].sort(compareExportNamesDigitLetterPinyin);

/**
 * 主要角色（ElementType.CAST）按 elementNO 排序的比较函数
 * 排序规则：
 * 1. 优先按 elementNO 排序
 * 2. 序号类型优先级：数字(0) > 字母(1) > 汉字(2)
 * 3. 同类型序号按值排序（数字按数值，字母按字母顺序，汉字按拼音）
 * 4. 如果序号相同，按名称排序
 * 5. 有序号的排在前面，没有序号的排在后面
 * 6. 都没有序号时，按名称排序
 * @param a 第一个场次元素（可以是 ElementWithUserNames 或 ElementSceneInfo）
 * @param b 第二个场次元素（可以是 ElementWithUserNames 或 ElementSceneInfo）
 * @returns 比较结果
 */
export const compareCastItemsByNO = (
  a: ElementWithUserNames | ElementSceneInfo | ElementInfo,
  b: ElementWithUserNames | ElementSceneInfo | ElementInfo,
): number => {
  const aHasNO = a.elementNO && a.elementNO.trim() !== '';
  const bHasNO = b.elementNO && b.elementNO.trim() !== '';
  
  // 如果都有序号，按统一规则排序：符号 > 数字 > 英文 > 汉字(拼音)
  if (aHasNO && bHasNO) {
    const aNO = a.elementNO!.trim();
    const bNO = b.elementNO!.trim();
    const noCompare = compareBySymbolNumberEnglishChinese(aNO, bNO);
    if (noCompare !== 0) {
      return noCompare;
    }
    
    // 如果序号相同，按显示名称排序（符号 > 数字 > 英文 > 汉字(拼音)）
    const nameA = (a.elementName || a.elementAbbr || '').trim();
    const nameB = (b.elementName || b.elementAbbr || '').trim();
    return compareExportNamesDigitLetterPinyin(nameA, nameB);
  }

  // 如果只有一个有序号，有序号的排在前面
  if (aHasNO && !bHasNO) return -1;
  if (!aHasNO && bHasNO) return 1;

  // 如果都没有序号，按显示名称排序（数字、英文、中文优先级）
  const nameA = (a.elementName || a.elementAbbr || '').trim();
  const nameB = (b.elementName || b.elementAbbr || '').trim();
  return compareExportNamesDigitLetterPinyin(nameA, nameB);
};

/**
 * 主要角色（ElementType.CAST）按 elementNO 排序
 * @param items 场次元素数组（可以是 ElementWithUserNames[] 或 ElementSceneInfo[]）
 * @returns 排序后的场次元素数组
 */
export const sortCastItemsByNO = <T extends ElementWithUserNames | ElementSceneInfo | ElementInfo>(
  items: T[],
): T[] => {
  return [...items].sort(compareCastItemsByNO);
};
